import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import ReactDOM from 'react-dom';
import React from 'react';
import cssVars from 'css-vars-ponyfill';
import { forEach, reduce, omit } from 'lodash';
import core, { App } from '@gin-betting/core';

import type {
    BettingSettingsOptions,
    BettingWidgetSettings,
    BettingWidgetsOptions,
    BreakpointSettings,
    EventPayloadsByType,
    IBettingAPI,
    IBettingEvents,
} from 'types';
import {
    BettingWidgets,
    DEFAULT_WIDGET_OPTIONS,
    ROOT_STYLE_ID_ELEMENT,
    EventType,
    Destination,
    classicIds,
    esportIds,
    PublicRoutes,
    SlugPublicRoutes,
    DEFAULT_BETTING_SETTINGS,
    BettingSettingsKeys,
} from 'app-constants';
import {
    IBettingPalette,
    IBettingPinnedStreamPosition,
    IBettingSpacing,
    DEFAULT_POSITION_PROPS,
    DEFAULT_FONT_MAPPING_BY_SUFFIX,
} from 'styles';
import {
    getPaletteCssVariables,
    checkWidgetContainerProvided,
    getPinnedStreamPositionCssVariables,
    getSpacingCssVariables,
} from 'utils';
import adaptBettingOptions from 'utils/adaptBettingOptions';
import { BettingConfigCtx } from './bettingConfigCtx';
import Root from './Root';
import createRoutes from '../services/createRoutes';

const {
    utils: { getBettingOptions, measureTime, isInnerPage },
    services: { getHistory, BettingAPI: BettingAPICore },
} = core;

interface BettingCssVariablesMap {
    palette: ReturnType<typeof getPaletteCssVariables>;
    spacing: CssVariables;
    pinnedStreamPosition: CssVariables;
}

class BettingAPI
    extends BettingAPICore<EventPayloadsByType, BettingSettingsOptions>
    implements IBettingAPI {
    private pathname: string = getHistory().location.pathname;
    private widgets$: BettingWidgetSettings = new BehaviorSubject(
        DEFAULT_WIDGET_OPTIONS
    );

    public breakpoints: BreakpointSettings;
    private cssVariablesMap: Partial<BettingCssVariablesMap> = {};

    constructor() {
        super({
            bettingSettings$: new BehaviorSubject(DEFAULT_BETTING_SETTINGS),
            requiredCallbacks: ['getBanners'],
            fonts: DEFAULT_FONT_MAPPING_BY_SUFFIX,
        });
        this.setupPalette({});
        this.setupSpacing({});

        this.cssVariablesMap.pinnedStreamPosition = DEFAULT_POSITION_PROPS;

        window.__app.version = __VERSION__;
        window.__app.initialTitle = document.title;

        const {
            url: { staticEndpoint },
        } = getBettingOptions();

        if (!__DEV__ && staticEndpoint) {
            __webpack_public_path__ = `${staticEndpoint}/`;
        }
    }

    setupPalette(palette: Partial<IBettingPalette>): void {
        this.cssVariablesMap.palette = getPaletteCssVariables(palette);

        if (this.isInitialized) this.insertCssVariables();
    }

    setupSpacing(spacing: Partial<IBettingSpacing>): void {
        this.cssVariablesMap.spacing = getSpacingCssVariables(spacing);

        if (this.isInitialized) this.insertCssVariables();

        if (typeof spacing.topOffset !== 'undefined') {
            this.setupWidget(BettingWidgets.CATEGORIZER, {});
        }
    }

    setupPinnedStreamPosition(
        positionVars: Required<IBettingPinnedStreamPosition>
    ): void {
        this.cssVariablesMap.pinnedStreamPosition = getPinnedStreamPositionCssVariables(
            positionVars
        );

        if (this.isInitialized) this.insertCssVariables();
    }

    setupBreakpoints(breakpoints: BreakpointSettings): void {
        const validationError = validateCustomBreakpoints(breakpoints);

        if (validationError) {
            console.error(validationError);

            return;
        }

        this.breakpoints = breakpoints;
    }

    setupWidget<T extends BettingWidgets>(
        widget: T,
        options: BettingWidgetsOptions[T]
    ): void {
        const validationError = validateSetupWidget(widget, options);

        if (validationError) {
            console.error(validationError);

            return;
        }

        const widgets = this.widgets$.getValue();
        this.widgets$.next({
            ...widgets,
            [widget]: {
                ...DEFAULT_WIDGET_OPTIONS[widget],
                ...widgets[widget],
                ...options,
            },
        });
    }

    updateBettingOptions = (pathname: string): void => {
        const {
            url: { pagePath },
        } = getBettingOptions();

        const isBettingPage = pagePath === '';

        if (!isBettingPage) return;

        const { destination, sportIds } = getRedirectPayloadByPathname(
            pathname
        );

        // @ts-ignore bettingOptions is exist on window but we can't show it outside. We extend bettingOptions by renamed name props.
        window.bettingOptions = adaptBettingOptions({
            ...getBettingOptions(),
            selectedSportIds: sportIds,
        });

        this.bettingEvents$.dispatch(EventType.Redirect, {
            destination,
            link: '',
        });

        if (__DEV__) {
            try {
                localStorage.setItem(
                    'bettingOptions',
                    JSON.stringify({
                        ...omit(
                            JSON.parse(
                                localStorage.getItem('bettingOptions') || '{}'
                            ),
                            ['selectedSportIds', 'sportIds']
                        ),
                    })
                );
            } catch (e) {
                console.error(e);
            }
        }
    };

    init(): void {
        super.init();
        this.insertCssVariables();

        const initPathname = getHistory().location.pathname;

        // TODO: fix for outright page
        if (
            initPathname.includes(App.Routes.OUTRIGHT) ||
            initPathname.includes(App.Routes.OUTRIGHTS_LIST)
        ) {
            this.updateBettingOptions(initPathname);
        }

        getHistory().listen(({ pathname }) => {
            if (pathname === this.pathname) return;

            this.pathname = pathname;

            // TODO: fix for outright page
            if (isInnerPage() && !pathname.includes(App.Routes.OUTRIGHT))
                return;

            this.updateBettingOptions(pathname);
        });
    }

    renderDOM = (rootEl: HTMLElement): void => {
        ReactDOM.render(
            <MainRoot rootEl={rootEl}>
                <BettingConfigCtx.Provider
                    value={{
                        subscribe: this.subscribeOnBettingSetting,
                        set: this.setBettingSetting,
                        widgets$: this.widgets$,
                        bettingEvents: this.bettingEvents$,
                    }}
                >
                    <Root breakpoints={this.breakpoints}>{createRoutes}</Root>
                </BettingConfigCtx.Provider>
            </MainRoot>,
            rootEl,
            () => {
                measureTime('rootRenderComplete');
                measurePageTimings();
            }
        );
    };

    private insertCssVariables() {
        const cssInString = reduce(
            this.cssVariablesMap,
            (acc, vars) =>
                vars ? acc + convertCssVariablesToString(vars) : acc,
            ''
        );

        const rootStyleHTMLElement = BettingAPI.getRootHTMLStyleElement();
        rootStyleHTMLElement.innerHTML = `:root {${cssInString}}`;
        document.head.appendChild(rootStyleHTMLElement);

        cssVars();
    }

    private static getRootHTMLStyleElement(): HTMLStyleElement {
        const existed = document.getElementById(ROOT_STYLE_ID_ELEMENT);

        if (existed?.tagName === 'STYLE') return existed as HTMLStyleElement;

        const rootHTMLStyleElement = document.createElement('style');
        rootHTMLStyleElement.id = ROOT_STYLE_ID_ELEMENT;

        return rootHTMLStyleElement;
    }

    public selectTheme = (theme: Themes): void => {
        this.setBettingSetting(BettingSettingsKeys.THEMES, theme);
    };

    public changeRoute = (path: string): undefined => {
        if (!path) return;

        if (
            PublicRoutes.includes(path) ||
            SlugPublicRoutes.some(url => path.startsWith(url))
        ) {
            const history = getHistory();
            history.push(path);
        } else {
            console.warn(
                'Invalid route!\n' +
                    ' Please consider using one of the public routes instead.\n' +
                    ' Note: routes that ends with slash require correct "slug" to be postfixed by!'
            );
            console.table(
                PublicRoutes.map((r, i) => ({
                    ['General routes']: r,
                    ['Slug routes']: SlugPublicRoutes[i] || '',
                }))
            );
        }
    };
}

function MainRoot({
    rootEl,
    children,
}: {
    rootEl: HTMLElement;
    children: JSX.Element;
}) {
    return rootEl.tagName === 'MAIN' ? children : <main>{children}</main>;
}

function convertCssVariablesToString(
    vars: CssVariables | ReturnType<typeof getPaletteCssVariables>
): string {
    return reduce(vars, (acc, value, prop) => `${acc}${prop}: ${value};`, '');
}

function measurePageTimings(delay: number = 1000): void {
    if (!window.__app || !window.__app.timings) return;

    setTimeout(() => {
        const times = window.timing.getTimes();
        const timeParams = [
            'firstPaintTime',
            'appcacheTime',
            'connectTime',
            'domReadyTime',
            'initDomTreeTime',
            'loadEventTime',
            'loadTime',
            'requestTime',
            'redirectTime',
        ];

        timeParams.forEach(param => {
            window.__app.timings[param] = times[param];
        });
    }, delay);
}

function validateCustomBreakpoints(
    breakpoints: BreakpointSettings
): Error | undefined {
    const keyForValidation: Array<keyof BreakpointSettings> = ['medium'];

    if (typeof breakpoints !== 'object') {
        return new Error(
            `Invalid argument. "Breakpoints" argument should be an object`
        );
    }

    const errors: string[] = [];

    forEach(breakpoints, (value, key) => {
        if (
            !keyForValidation.includes(key as keyof BreakpointSettings) ||
            typeof value !== 'number'
        )
            errors.push(`Invalid argument "${key}"`);
    });

    if (errors.length > 0) {
        return new Error(`\n${errors.join('\n')}`);
    }
}

function validateSetupWidget<T extends BettingWidgets>(
    widget: T,
    options: BettingWidgetsOptions[T]
): Error | undefined {
    if (!DEFAULT_WIDGET_OPTIONS[widget]) {
        return new Error(`Not supported widget "${widget}"`);
    }

    if (
        checkWidgetContainerProvided(options) &&
        options.containerElementId !== null &&
        typeof options.containerElementId !== 'string'
    ) {
        return new Error('Invalid argument "containerElementId"');
    }

    if (widget === BettingWidgets.BETSLIP_SIDEBAR) {
        return validateBetslipSidebarWidgetOptions(
            options as BettingWidgetsOptions[BettingWidgets.BETSLIP_SIDEBAR]
        );
    }

    if (widget === BettingWidgets.STREAM) {
        return validateStreamWidgetOptions(
            options as BettingWidgetsOptions[BettingWidgets.STREAM]
        );
    }
}

function validateBetslipSidebarWidgetOptions(
    options: BettingWidgetsOptions[BettingWidgets.BETSLIP_SIDEBAR]
): Error | undefined {
    if (
        options.canToggle !== undefined &&
        typeof options.canToggle !== 'boolean'
    ) {
        return new Error('Invalid argument "canToggle"');
    }

    if (options.isOpen !== undefined && typeof options.isOpen !== 'boolean') {
        return new Error('Invalid argument "isOpen"');
    }
}

function validateStreamWidgetOptions({
    scrollToFirstStream,
    disableMatchTracker,
    disableTheaterMode,
    pinMinimizedStreamOutOfView,
}: BettingWidgetsOptions[BettingWidgets.STREAM]): Error | undefined {
    if (
        scrollToFirstStream !== undefined &&
        typeof scrollToFirstStream !== 'boolean'
    ) {
        return new Error('Invalid argument "scrollToFirstStream"');
    }

    if (
        disableMatchTracker !== undefined &&
        typeof disableMatchTracker !== 'boolean' &&
        !Array.isArray(disableMatchTracker)
    ) {
        return new Error('Invalid argument "disableMatchTracker"');
    }

    if (
        disableTheaterMode !== undefined &&
        typeof disableTheaterMode !== 'boolean'
    ) {
        return new Error('Invalid argument "disableTheaterMode"');
    }

    if (
        pinMinimizedStreamOutOfView !== undefined &&
        typeof pinMinimizedStreamOutOfView !== 'boolean'
    ) {
        return new Error('Invalid argument "pinMinimizedStreamOutOfView"');
    }
}

function getRedirectPayloadByPathname(
    pathname: string
): {
    destination: Destination;
    sportIds: string[];
} {
    switch (true) {
        case pathname.includes(App.Routes.OUTRIGHTS_LIST):
        case pathname.includes(App.Routes.OUTRIGHT): {
            return {
                destination: Destination.Outright,
                sportIds: [...classicIds, ...esportIds],
            };
        }

        case pathname.includes(App.Routes.CLASSIC): {
            return {
                destination: Destination.Sport,
                sportIds: classicIds,
            };
        }

        case pathname.includes(App.Routes.ESPORT): {
            return {
                destination: Destination.Esport,
                sportIds: esportIds,
            };
        }

        case pathname === App.Routes.LIVE_LIST: {
            return {
                destination: Destination.Live,
                sportIds: [...classicIds, ...esportIds],
            };
        }

        case pathname.includes(App.Routes.RESULTS_LIST): {
            return {
                destination: Destination.Results,
                sportIds: [...classicIds, ...esportIds],
            };
        }

        default: {
            return {
                destination: Destination.MixSports,
                sportIds: [...classicIds, ...esportIds],
            };
        }
    }
}

export default BettingAPI;
